import React, { useState } from 'react';
import { Avatar, Typography, Box, Divider, Button, CircularProgress, Grid, Menu, MenuItem } from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { makeStyles } from '@mui/styles';
import { useAuth } from '../../../contexts/authContext';

const useStyles = makeStyles((theme) => ({
  profileBox: {
    padding: theme.spacing(1),
    minWidth: 240,
    maxWidth: '100%',
    width: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  avatar: {
    marginBottom: theme.spacing(1),
  },
  email: {
    fontWeight: 'bold',
    fontSize: '1rem',
    textAlign: 'left',
    maxWidth: '100%',
  },
  role: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    textAlign: 'left',
  },
  divider: {
    margin: theme.spacing(1, 0),
    width: '100%',
  },
  switchRoleButton: {
    textTransform: 'none',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    height: '40px',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      marginLeft: theme.spacing(1),
    },
  },
  logoutButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
    textTransform: 'none',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    height: '45px',
    position: 'relative',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  buttonContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  circularProgress: {
    position: 'absolute',
  },
}));

const ProfileBox = ({ email, role, roles, profileImage, onLogout, onSwitchRole, avatarColor, initials, handleProfileClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);


  const handleLogout = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onLogout();
    }, 2000);
  };

  const getAlternateRole = () => {
    return roles.find((r) => r !== role);
  };

  const handleSwitchRole = async () => {
    const newRole = getAlternateRole();
    if (!newRole) return;

    try {
      setIsSwitching(true);
      await onSwitchRole(newRole);
      handleProfileClose();
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <Box className={classes.profileBox}>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          {profileImage ? (
            <Avatar className={classes.avatar} src={profileImage} style={{ backgroundColor: avatarColor }} />
          ) : (
            <Avatar className={classes.avatar} style={{ backgroundColor: avatarColor }}>
              {initials}
            </Avatar>
          )}
        </Grid>
        <Grid item xs>
          <Typography variant="body1" className={classes.email}>
            {email}
          </Typography>
          <Typography variant="body2" className={classes.role}>
            {role}
          </Typography>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />

      {roles.length > 1 && (
        <Button
          variant="outlined"
          className={classes.switchRoleButton}
          onClick={handleSwitchRole}
          disabled={isSwitching}
        >
          {isSwitching ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{ padding: "8px", color: "text.primary" }}
            >
              <CircularProgress color="inherit" size={20} />
              <Typography variant="body2">Switching...</Typography>
            </Box>
          ) : (
            <>
              {`Switch to ${getAlternateRole()}`}
              <SwapHorizIcon />
            </>
          )}
        </Button>
      )}

      <Button
        variant="contained"
        onClick={handleLogout}
        className={classes.logoutButton}
        disabled={loading}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          minWidth: '100px',
        }}
      >
        {loading ? (
          <>
            <span>Logout</span>
            <CircularProgress color="inherit" size={15} />
          </>
        ) : (
          <>
            <span>Logout</span>
            <ExitToAppIcon fontSize="small" />
          </>
        )}
      </Button>
    </Box>
  );
};

export default ProfileBox;
